import {
  CheckCircle,
  Clock,
  DollarSign,
  TrendingUp,
  XCircle,
} from "lucide-react"
import CountUp from "react-countup"
import { useTranslation } from "react-i18next"

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"

interface MetricData {
  value: number
  isLoading: boolean
}

interface FinancialHealthProps {
  futureReceivables: MetricData
  installmentsReceivable?: MetricData
  installmentsPaid?: MetricData
  installmentsRefused?: MetricData
}

const FinancialMetricCard = ({
  title,
  value,
  icon: Icon,
  isLoading,
  iconColor,
  borderColor,
  textColor,
}: {
  title: string
  value: number
  icon: React.ElementType
  isLoading: boolean
  iconColor: string
  borderColor: string
  textColor: string
}) => {
  const { t } = useTranslation()
  const hasData =
    value !== null && value !== undefined && value > 0

  return (
    <Card
      className={`border-l-4 ${borderColor} transition-all duration-200 hover:shadow-md`}
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
        <Icon className={`h-4 w-4 ${iconColor}`} />
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-8 w-32" />
        ) : !hasData ? (
          <div className="text-sm text-muted-foreground">
            {t("pages.dashboard.noDataAvailable")}
          </div>
        ) : (
          <CountUp
            start={0}
            end={value}
            separator="."
            decimals={2}
            decimal=","
            prefix="R$ "
            className={`text-2xl font-bold ${textColor}`}
          />
        )}
      </CardContent>
    </Card>
  )
}

export const FinancialHealth: React.FC<
  FinancialHealthProps
> = ({
  futureReceivables,
  installmentsReceivable,
  installmentsPaid,
  installmentsRefused,
}) => {
  const { t } = useTranslation()

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="rounded-lg bg-emerald-500/10 p-2">
          <DollarSign className="h-5 w-5 text-emerald-600" />
        </div>
        <div>
          <h2 className="text-lg font-semibold">
            {t(
              "pages.dashboard.sections.financialHealth.title",
              "Saúde Financeira"
            )}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t(
              "pages.dashboard.sections.financialHealth.description",
              "Visão do fluxo de caixa e recebíveis"
            )}
          </p>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <FinancialMetricCard
          title={t(
            "pages.dashboard.cards.futureReceivables.title"
          )}
          value={futureReceivables.value}
          isLoading={futureReceivables.isLoading}
          icon={TrendingUp}
          iconColor="text-emerald-500"
          borderColor="border-l-emerald-500"
          textColor="text-emerald-700"
        />
        {installmentsPaid && (
          <FinancialMetricCard
            title={t(
              "pages.dashboard.cards.installmentsPaid.title"
            )}
            value={installmentsPaid.value}
            isLoading={installmentsPaid.isLoading}
            icon={CheckCircle}
            iconColor="text-green-500"
            borderColor="border-l-green-500"
            textColor="text-green-700"
          />
        )}
        {installmentsReceivable && (
          <FinancialMetricCard
            title={t(
              "pages.dashboard.cards.installmentsReceivable.title"
            )}
            value={installmentsReceivable.value}
            isLoading={installmentsReceivable.isLoading}
            icon={Clock}
            iconColor="text-blue-500"
            borderColor="border-l-blue-500"
            textColor="text-blue-700"
          />
        )}
        {installmentsRefused && (
          <FinancialMetricCard
            title={t(
              "pages.dashboard.cards.installmentsRefused.title"
            )}
            value={installmentsRefused.value}
            isLoading={installmentsRefused.isLoading}
            icon={XCircle}
            iconColor="text-red-500"
            borderColor="border-l-red-500"
            textColor="text-red-700"
          />
        )}
      </div>
    </div>
  )
}
